import { observable, action, runInAction, makeObservable } from 'mobx';
import { IApiClient } from '~/lib/ApiClient';
import {
    CreateAndPayForServerResult,
    CreateServerRequest,
    PrePaymentFrequency,
    PrePaymentFrequencyOption,
} from '~/lib/ApiModel';
import { getAppContext } from '~/lib/AppContext';

import {
    getChildErrors,
    isValidationProblemDetails,
    removeError,
    ValidationErrors,
    buildValidationErrorDetails,
} from '~/lib/helper/ErrorHelper';
import { ISiteServicesApiClient } from '~/lib/SiteServicesApiClient';
import { ServerSizeConfigStore } from '~/lib/store/ServerSizeConfigStore';
import UnpaidInvoiceStore from '~/lib/store/UnpaidInvoiceStore';

const DEFAULT_PAYMENT_FREQUENCY: PrePaymentFrequency = 'monthly';

export default class ServerAddStore extends ServerSizeConfigStore {
    @observable
    errorRequest?: CreateServerRequest = undefined;

    @observable
    selectedNetworkId?: number = undefined;

    @observable
    vpcIpv4Address = '';

    @observable
    separatePrivateNetworkInterface = false;

    @observable
    selectedPaymentFrequency?: PrePaymentFrequency = undefined;

    @observable
    showAdvanced = false;

    unpaidInvoices: UnpaidInvoiceStore;

    siteApiClient: ISiteServicesApiClient;

    constructor(apiClient: IApiClient, siteApiClient: ISiteServicesApiClient) {
        super(apiClient);
        this.siteApiClient = siteApiClient;
        this.unpaidInvoices = new UnpaidInvoiceStore(this.apiClient);
        makeObservable(this);
    }

    @action
    selectNetwork(id: number | undefined) {
        this.currentErrors = removeError(this.currentErrors, 'network_id');
        this.selectedNetworkId = id;
        this.vpcIpv4Address = '';
        this.separatePrivateNetworkInterface = false;
        this.currentErrors = removeError(this.currentErrors, 'vpc_ipv4_address');
    }

    @action
    setVpcIpv4Address(ip: string) {
        this.vpcIpv4Address = ip;
        this.currentErrors = removeError(this.currentErrors, 'vpc_ipv4_address');
    }

    @action
    setSeparatePrivateNetworkInterface(enabled: boolean) {
        this.separatePrivateNetworkInterface = enabled;
        this.currentErrors = removeError(this.currentErrors, 'separate_private_network_interface');
    }

    @action
    selectPaymentFrequency(freq: PrePaymentFrequency) {
        this.selectedPaymentFrequency = freq;
        this.currentErrors = removeError(this.currentErrors, 'payment_frequency');
    }

    get selectedPaymentFrequencyOption(): PrePaymentFrequencyOption | undefined {
        if (!getAppContext().isEnabled('PaymentFrequency')) return undefined;

        const chosen = this.serverOptions.paymentFrequencies.items.find(
            (pf) => pf.pre_payment_frequency === this.selectedPaymentFrequency
        );

        // return selected item if we can find it, default to monthly if we can find that, or the first item otherwise (or undefined)
        return (
            chosen ??
            this.serverOptions.paymentFrequencies.items.find(
                (pf) => pf.pre_payment_frequency === DEFAULT_PAYMENT_FREQUENCY
            ) ??
            this.serverOptions.paymentFrequencies.items[0] ??
            undefined
        );
    }

    @action
    clearErrors() {
        this.currentErrors = {};
        this.errorRequest = undefined;
    }

    asCreateServerRequest(): CreateServerRequest {
        return {
            name: this.hostname === '' ? null : this.hostname,
            size: this.selectedSize!.slug,
            image: this.selectedOperatingSystem!.slug!,
            region: this.selectedRegion!.slug,
            vpc_id: this.selectedNetworkId,
            vpc_ipv4_address: this.selectedNetworkId && this.vpcIpv4Address ? this.vpcIpv4Address : null,
            separate_private_network_interface: this.selectedNetworkId
                ? this.separatePrivateNetworkInterface
                : undefined,
            ssh_keys: this.selectedSshKeysIds,
            options: this.selectedSizeOptions,
            licenses: this.selectedLicenses,
            user_data: this.selectedUserData,
            pre_payment_frequency: this.selectedPaymentFrequencyOption?.pre_payment_frequency,
        };
    }

    async addServer(): Promise<CreateAndPayForServerResult> {
        let req: CreateServerRequest | undefined = undefined;
        try {
            this.clearErrors();

            /* We do some client side validation here that isn't required for API users */
            const localErrors: ValidationErrors = {};

            if (!this.agreeTerms) {
                localErrors['agreeTerms'] = ['You must accept these terms before continuing'];
            }

            if (!this.selectedSize?.slug) {
                localErrors['size'] = ['You must select a plan'];
            }

            if (!this.selectedRegion?.slug) {
                localErrors['region'] = ['You must select a region'];
            }

            if (!this.selectedOperatingSystem?.slug) {
                localErrors['image'] = ['You must select an Operating System'];
            }

            // only validate backup options if a plan is selected (no plan = no backup context)
            if (this.selectedSize) {
                const opts = this.selectedSizeOptions;
                if (
                    opts?.daily_backups === undefined &&
                    opts?.weekly_backups === undefined &&
                    opts?.monthly_backups === undefined
                ) {
                    localErrors['options.daily_backups'] = ['You must select a backup option'];
                }
            }

            // eslint-disable-next-line @typescript-eslint/only-throw-error
            if (Object.keys(localErrors).length > 0) throw buildValidationErrorDetails(localErrors);

            req = this.asCreateServerRequest();

            const baseUrl = window.location.origin;
            const res = await this.siteApiClient.createServer({
                server_details: req,
                paypal_return_info: {
                    transaction_success_url: `${baseUrl}/payment/complete`,
                    paypal_success_return_url: `${baseUrl}/payment/paypal/process`,
                    paypal_cancel_return_url: `${baseUrl}/payment/paypal/cancel`,
                },
            });
            return res;
        } catch (e) {
            if (isValidationProblemDetails(e)) {
                // The request is nested under server_details, so the API keys the server field errors that
                // way while the form reads them unprefixed. Merge rather than replace: the client-side
                // errors built above are already unprefixed, and replacing would discard them.
                const errs = { ...e.errors, ...getChildErrors(e.errors, 'server_details') };
                runInAction(() => {
                    this.currentErrors = errs;
                    this.errorRequest = req;
                });
            }
            throw e;
        }
    }
}
